import { random } from '../number/number';

export function compose(...rest: any[]) {
    return (x: any) => rest.reduceRight((y, f) => f(y), x);
}

export function getCombinations<T>(items: T[], length: number, list: T[] = []): T[][] {
    // Check values
    if (!items.length || length <= 0) {
        return [];
    }

    // Check list
    if (list.length === length) {
        return [list];
    }

    // Add combinations
    return items.reduce((combinations, item) => {
        // Get child combinations
        const childCombinations = getCombinations(items, length, [...list, item]);

        // Merge combinations
        return [...combinations, ...childCombinations];
    }, [] as T[][]);
}

export function getTime() {
    return new Date().getTime();
}

export function getRandom<T>(items: T[]) {
    const index = random(0, items.length - 1, true);
    return items[index];
}

export function getShallowEqual(a: any, b: any) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    return aKeys.length === bKeys.length && aKeys.every(key => a[key] === b[key]);
}

export function getUnique<T>(items: T[]) {
    const set = new Set(items);
    return Array.from(set);
}

export function getPathname(asPath: string): string {
    const pathWithoutHash = asPath.split('#')[0] || '/';
    const pathWithoutQuery = pathWithoutHash.split('?')[0] || '/';
    return pathWithoutQuery || '/';
}

export function getUrl(path: string) {
    if (path.startsWith('http')) {
        return path;
    }
    return `${process.env.NEXT_CONFIG_BASE_PATH || ''}${path}`;
}

export function getKromaticSiteOriginForHost(hostname?: string | null): string | null {
    const host = (hostname || '').toLowerCase();

    if (host === 'staging.kromatic.com') {
        return 'https://staging.kromatic.com';
    }

    if (host === 'kromatic.com' || host === 'www.kromatic.com') {
        return 'https://kromatic.com';
    }

    return null;
}

const DEFAULT_PLINKROMATIC_ORIGIN = 'https://kromatic.com';
const STAGING_PLINKROMATIC_ORIGIN = 'https://staging.kromatic.com';
const DEFAULT_PLINKROMATIC_BASE_PATH = '/tools/innovation-management';

export const DEFAULT_AUTH_API_URL = 'https://api.kromatic.com';
export const STAGING_AUTH_API_URL = 'https://staging-api.kromatic.com';

/** When the app is served from staging, return staging auth API base so OAuth and /user/info use staging. */
export function getAuthApiBaseFromHost(hostname?: string | null): string | null {
    const host = (hostname || '').toLowerCase().split(':')[0];
    if (host === 'staging.kromatic.com') {
        return STAGING_AUTH_API_URL;
    }
    return null;
}

type SeoEnvironment = Partial<Record<string, string | undefined>>;

function getNormalizedOrigin(url?: string) {
    if (!url) {
        return null;
    }

    try {
        const parsed = new URL(url);
        return `${parsed.protocol}//${parsed.host}`;
    } catch {
        return null;
    }
}

function normalizeBasePath(path?: string) {
    const candidate = path || DEFAULT_PLINKROMATIC_BASE_PATH;
    if (!candidate) {
        return DEFAULT_PLINKROMATIC_BASE_PATH;
    }

    if (candidate === '/') {
        return '/';
    }

    const prefixed = candidate.startsWith('/') ? candidate : `/${candidate}`;
    return prefixed.replace(/\/+$/, '');
}

function stripQueryAndHash(path: string) {
    const [pathname = '/'] = path.split(/[?#]/);
    return pathname || '/';
}

export function getPlinkromaticSiteOrigin(env: SeoEnvironment = process.env): string {
    const explicitOrigin = getNormalizedOrigin(env.NEXT_PUBLIC_SITE_ORIGIN || env.NEXT_PUBLIC_APP_URL);
    if (explicitOrigin) {
        return explicitOrigin;
    }

    const envName = [
        env.NEXT_PUBLIC_APP_ENV,
        env.APP_ENV,
        env.NEXT_PUBLIC_VERCEL_ENV,
        env.VERCEL_ENV,
        env.NODE_ENV,
    ]
        .find(Boolean)
        ?.toLowerCase();

    if (envName === 'staging' || envName === 'preview') {
        return STAGING_PLINKROMATIC_ORIGIN;
    }

    return DEFAULT_PLINKROMATIC_ORIGIN;
}

/** True when the app is running on staging (shared staging noindex policy applies). */
export function isPlinkromaticStaging(env: SeoEnvironment = process.env): boolean {
    return getPlinkromaticSiteOrigin(env) === STAGING_PLINKROMATIC_ORIGIN;
}

export function getPlinkromaticCanonicalUrl(env: SeoEnvironment = process.env): string {
    const origin = getPlinkromaticSiteOrigin(env);
    const basePath = normalizeBasePath(env.NEXT_CONFIG_BASE_PATH);

    if (basePath === '/') {
        return origin;
    }

    return `${origin}${basePath}`;
}

export function getPlinkromaticRoutePath(path: string, env: SeoEnvironment = process.env): string {
    const candidate = stripQueryAndHash(path || '/');
    const basePath = normalizeBasePath(env.NEXT_CONFIG_BASE_PATH);
    const normalizedCandidate = candidate.startsWith('/') ? candidate : `/${candidate}`;

    if (basePath !== '/' && normalizedCandidate.startsWith(basePath)) {
        const stripped = normalizedCandidate.slice(basePath.length) || '/';
        return stripped.startsWith('/') ? stripped : `/${stripped}`;
    }

    return normalizedCandidate;
}

export function getPlinkromaticRelativePath(path: string, env: SeoEnvironment = process.env): string {
    return getPlinkromaticRoutePath(path, env);
}

export function getPlinkromaticCanonicalUrlForPath(path: string, env: SeoEnvironment = process.env): string {
    const canonicalUrl = getPlinkromaticCanonicalUrl(env);
    const routePath = getPlinkromaticRoutePath(path, env);

    if (routePath === '/') {
        return canonicalUrl;
    }

    return `${canonicalUrl}${routePath}`;
}

export function resolveKromaticSiteUrl(url: string, runtimeHostname?: string): string {
    if (!url) {
        return url;
    }

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }

    const host = parsed.hostname.toLowerCase();
    const isKnownKromaticHost =
        host === 'kromatic.com' || host === 'www.kromatic.com' || host === 'staging.kromatic.com';
    if (!isKnownKromaticHost) {
        return url;
    }

    const resolvedOrigin = getKromaticSiteOriginForHost(
        runtimeHostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
    );
    if (!resolvedOrigin) {
        return url;
    }

    const target = new URL(resolvedOrigin);
    parsed.protocol = target.protocol;
    parsed.host = target.host;
    return parsed.toString();
}

export function hexToDec(hex: string) {
    return parseInt(hex.replace('#', ''), 16);
}

export function validateEmail(email: string) {
    const normalizedEmail = email.trim();
    const emailRegex =
        /^[^\s@]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
    return emailRegex.exec(normalizedEmail);
}
